import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import rewardsBannerBg from '@sorare/core/src/assets/rewards/rewards-banner-bg.webp';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RewardsFlow } from 'components/so5/LastFixtureStory/RewardsFlow';
import { useRewardsVerification } from 'components/so5/LastFixtureStory/RewardsRecap/useRewardsVerification';

import type { NewRewardsBanner_so5Reward } from './__generated__/index.graphql';

const StyledBadge = styled(Badge)`
  width: 100%;
`;

const Root = styled.button`
  display: block;
  width: 100%;
  background: no-repeat left / cover url(${rewardsBannerBg});
  padding: var(--triple-unit);
  border: 2px solid var(--c-white);
  border-radius: var(--double-unit);
  text-align: start;

  @media ${tabletAndAbove} {
    background-position: center;
  }
`;
const NotAlignedVertical = styled(Vertical)`
  align-items: flex-start;
`;

type Props = {
  so5Rewards: NewRewardsBanner_so5Reward[];
  Wrapper?: FC<{ children: ReactNode }>;
};
export const NewRewardsBanner = ({ so5Rewards, Wrapper }: Props) => {
  const { unclaimedRewardsByType } = useRewardsVerification(so5Rewards);
  const [showRewardsFlow, setShowRewardsFlow] = useState(false);

  const content = (
    <StyledBadge
      badgeContent={so5Rewards.length}
      className={classNames({
        withBadge: so5Rewards.length > 0,
      })}
    >
      <Root onClick={() => setShowRewardsFlow(true)}>
        <SBHorizontal>
          <NotAlignedVertical gap={0}>
            <LabelL bold>
              <FormattedMessage
                id="RewardsBanner.title"
                defaultMessage="New Rewards"
              />
            </LabelL>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="RewardsBanner.subtitle"
                defaultMessage="Your results are in!"
              />
            </LabelM>
          </NotAlignedVertical>
          <Button size="medium" asSpan>
            <FormattedMessage {...glossary.claimNow} />
          </Button>
        </SBHorizontal>
      </Root>
    </StyledBadge>
  );

  return (
    <>
      {!!showRewardsFlow && (
        <RewardsFlow
          initialStep="cardReveal"
          so5Rewards={so5Rewards}
          onClose={() => setShowRewardsFlow(false)}
        />
      )}
      {unclaimedRewardsByType.total > 0 && (
        <>{Wrapper ? <Wrapper>{content}</Wrapper> : content}</>
      )}
    </>
  );
};

NewRewardsBanner.fragments = {
  so5Reward: gql`
    fragment NewRewardsBanner_so5Reward on So5Reward {
      slug
      id
      ...RewardsFlow_so5Reward
    }
    ${RewardsFlow.fragments.so5Reward}
  ` as TypedDocumentNode<NewRewardsBanner_so5Reward>,
};
